import { useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { StudentRow, UserRow } from '@/lib/types';
import { initials, formatDate, titleCase, fullName } from '@/lib/format';
import ImportExport from '../components/ImportExport';
import { STUDENT_CSV_COLUMNS } from '@/lib/csv';

const statusColors: Record<string, string> = {
  Lead: 'bg-amber-100 text-amber-700',
  New: 'bg-amber-100 text-amber-700',
  Applied: 'bg-blue-100 text-blue-700',
  Visa: 'bg-purple-100 text-purple-700',
  Converted: 'bg-green-100 text-green-700',
};

const tabs = ['All', 'Lead', 'Applied', 'Visa', 'Converted'];

interface StudentView {
  id: number;
  name: string;
  phone: string;
  counselor: string;
  destination: string;
  status: string;
  lastUpdated: string;
  avatar: string;
}

function toView(row: StudentRow): StudentView {
  return {
    id: row.id,
    name: fullName(row.firstName, row.lastName),
    phone: row.phone,
    counselor: row.counselorName || 'Unassigned',
    destination: row.destinationCountry || '—',
    status: titleCase(row.status),
    lastUpdated: formatDate(row.updatedAt),
    avatar: initials(fullName(row.firstName, row.lastName)),
  };
}

const emptyForm = {
  firstName: '', lastName: '', gender: '', dateOfBirth: '', nationality: '',
  phone: '', alternatePhone: '', email: '', currentCity: '',
  parentName: '', parentPhone: '',
  destinationCountry: '', preferredIntake: '', studyLevel: '', preferredCourse: '', budget: '', fundingSource: '',
  highestQualification: '', qualificationGrade: '', englishTest: '', englishTestScore: '',
  passportNumber: '', passportExpiry: '',
  source: '', assignedCounselorId: '', status: 'lead', notes: '',
};

function AddStudentModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { data: users } = useApiData<UserRow[]>(() => api.users());
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.createStudent({ ...form, assignedCounselorId: form.assignedCounselorId ? Number(form.assignedCounselorId) : null });
      onCreated();
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not create student');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-5 border-b border-[#E1E5EA] flex justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-[#3C3C3C]">Add Student</h3>
            <p className="text-xs text-gray-400 mt-1">Capture the full student profile and assign a counselor.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 cursor-pointer"><i className="ri-close-line text-xl" /></button>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto">
          <Section title="Personal">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name *"><input required value={form.firstName} onChange={set('firstName')} placeholder="e.g. Arjun" className="input" /></Field>
              <Field label="Last name *"><input required value={form.lastName} onChange={set('lastName')} placeholder="e.g. Mehta" className="input" /></Field>
              <Field label="Gender"><select value={form.gender} onChange={set('gender')} className="input"><option value="">Select…</option><option>Male</option><option>Female</option><option>Other</option></select></Field>
              <Field label="Date of birth"><input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} className="input" /></Field>
              <Field label="Nationality"><input value={form.nationality} onChange={set('nationality')} placeholder="e.g. Indian" className="input" /></Field>
              <Field label="Current city"><input value={form.currentCity} onChange={set('currentCity')} placeholder="e.g. Hyderabad" className="input" /></Field>
            </div>
          </Section>

          <Section title="Contact & Guardian">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Phone *"><input required value={form.phone} onChange={set('phone')} placeholder="+91 98XXX XXXXX" className="input" /></Field>
              <Field label="Alternate phone"><input value={form.alternatePhone} onChange={set('alternatePhone')} placeholder="Optional" className="input" /></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={set('email')} placeholder="student@email.com" className="input" /></Field>
              <div />
              <Field label="Parent / Guardian name"><input value={form.parentName} onChange={set('parentName')} placeholder="e.g. Rajiv Mehta" className="input" /></Field>
              <Field label="Parent / Guardian phone"><input value={form.parentPhone} onChange={set('parentPhone')} placeholder="+91 98XXX XXXXX" className="input" /></Field>
            </div>
          </Section>

          <Section title="Study Preferences">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Destination country"><input value={form.destinationCountry} onChange={set('destinationCountry')} placeholder="e.g. United Kingdom" className="input" /></Field>
              <Field label="Preferred intake"><input value={form.preferredIntake} onChange={set('preferredIntake')} placeholder="e.g. Sep 2026" className="input" /></Field>
              <Field label="Study level"><select value={form.studyLevel} onChange={set('studyLevel')} className="input"><option value="">Select…</option><option>Undergraduate</option><option>Postgraduate</option><option>Diploma</option><option>PhD</option><option>Foundation</option></select></Field>
              <Field label="Preferred course / field"><input value={form.preferredCourse} onChange={set('preferredCourse')} placeholder="e.g. MSc Computer Science" className="input" /></Field>
              <Field label="Budget"><input value={form.budget} onChange={set('budget')} placeholder="e.g. ₹25–35 lakh" className="input" /></Field>
              <Field label="Funding source"><select value={form.fundingSource} onChange={set('fundingSource')} className="input"><option value="">Select…</option><option>Self-funded</option><option>Education Loan</option><option>Family</option><option>Scholarship</option><option>Mixed</option></select></Field>
            </div>
          </Section>

          <Section title="Academics & Tests">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Highest qualification"><input value={form.highestQualification} onChange={set('highestQualification')} placeholder="e.g. B.Tech CSE" className="input" /></Field>
              <Field label="Grade / Percentage"><input value={form.qualificationGrade} onChange={set('qualificationGrade')} placeholder="e.g. 8.4 CGPA / 82%" className="input" /></Field>
              <Field label="English test"><select value={form.englishTest} onChange={set('englishTest')} className="input"><option value="">None / Not taken</option><option>IELTS</option><option>TOEFL</option><option>PTE</option><option>Duolingo</option></select></Field>
              <Field label="Test score"><input value={form.englishTestScore} onChange={set('englishTestScore')} placeholder="e.g. 7.5" className="input" /></Field>
              <Field label="Passport number"><input value={form.passportNumber} onChange={set('passportNumber')} placeholder="Optional" className="input" /></Field>
              <Field label="Passport expiry"><input type="date" value={form.passportExpiry} onChange={set('passportExpiry')} className="input" /></Field>
            </div>
          </Section>

          <Section title="Assignment & Source">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Assigned counselor"><select value={form.assignedCounselorId} onChange={set('assignedCounselorId')} className="input"><option value="">Unassigned</option>{(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}</select></Field>
              <Field label="Lead source"><select value={form.source} onChange={set('source')} className="input"><option value="">Select…</option><option>Walk-in</option><option>Referral</option><option>Website</option><option>Social Media</option><option>Education Fair</option><option>Other</option></select></Field>
              <Field label="Status"><select value={form.status} onChange={set('status')} className="input"><option value="lead">Lead</option><option value="applied">Applied</option><option value="visa">Visa</option><option value="converted">Converted</option></select></Field>
              <div />
              <div className="sm:col-span-2"><Field label="Notes"><textarea value={form.notes} onChange={set('notes')} rows={2} placeholder="Any additional context…" className="input resize-none" /></Field></div>
            </div>
          </Section>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 flex-shrink-0 border-t border-[#E1E5EA]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 cursor-pointer">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 bg-[#0B6E77] text-white rounded-lg text-sm font-semibold disabled:opacity-50 cursor-pointer">{saving ? 'Saving…' : 'Add Student'}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-sm font-medium text-[#3C3C3C] mb-1.5">{label}</label>{children}</div>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">{title}</h4>{children}</div>;
}

export default function StudentsPage() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApiData<StudentRow[]>(() => api.students());
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const students = useMemo(() => (data ?? []).map(toView), [data]);

  const filtered = students.filter((s) => {
    const matchTab = activeTab === 'All' || s.status.toLowerCase() === activeTab.toLowerCase();
    const term = search.toLowerCase();
    const matchSearch =
      s.name.toLowerCase().includes(term) ||
      s.destination.toLowerCase().includes(term) ||
      s.counselor.toLowerCase().includes(term);
    return matchTab && matchSearch;
  });

  return (
    <DashboardLayout>
      <style>{`.input{width:100%;padding:0.625rem 0.875rem;border:1px solid #e5e7eb;border-radius:0.5rem;font-size:0.875rem;outline:none;background:#fff}.input:focus{border-color:#0B6E77}`}</style>
      <div className="space-y-4">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 bg-white border border-[#E1E5EA] rounded-lg px-3 py-2 w-full sm:w-64">
            <div className="w-4 h-4 flex items-center justify-center text-gray-400"><i className="ri-search-line text-sm" /></div>
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-sm text-gray-600 placeholder-gray-400 outline-none w-full"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ImportExport
              entity="students"
              label="Students"
              columns={STUDENT_CSV_COLUMNS}
              rows={(data ?? []) as unknown as Record<string, unknown>[]}
              onImport={(parsed) => api.bulkCreateStudents(parsed).then((r) => r.data)}
              onDone={reload}
            />
            <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
              Add Student
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 bg-white border border-[#E1E5EA] rounded-xl p-1 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab ? 'bg-[#0B6E77] text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
              <span className="ml-1.5 text-xs opacity-70">
                ({tab === 'All' ? students.length : students.filter((s) => s.status.toLowerCase() === tab.toLowerCase()).length})
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
          {loading && <div className="p-10 text-center text-sm text-gray-400">Loading students…</div>}
          {!loading && error && (
            <div className="p-10 text-center">
              <p className="text-sm text-red-600">{error}</p>
              <button onClick={reload} className="mt-3 px-4 py-2 text-sm bg-[#0B6E77] text-white rounded-lg cursor-pointer">Retry</button>
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="p-12 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center text-gray-400"><i className="ri-user-line text-xl" /></div>
              <p className="mt-3 text-sm font-medium text-gray-600">No students yet</p>
              <p className="text-xs text-gray-400 mt-1">Add your first student to get started.</p>
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E1E5EA] bg-gray-50">
                    {['Student', 'Phone', 'Counselor', 'Destination', 'Status', 'Last Updated', 'Actions'].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-500 px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/dashboard/students/${s.id}`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex-shrink-0">{s.avatar}</div>
                          <span className="text-sm font-medium text-gray-800 whitespace-nowrap">{s.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{s.phone}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{s.counselor}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{s.destination}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${statusColors[s.status] || 'bg-gray-100 text-gray-600'}`}>{s.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">{s.lastUpdated}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button onClick={() => navigate(`/dashboard/students/${s.id}`)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-blue-50 text-blue-500 cursor-pointer"><i className="ri-eye-line text-sm" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAdd && <AddStudentModal onClose={() => setShowAdd(false)} onCreated={reload} />}
    </DashboardLayout>
  );
}
